import React, {
  FC,
  ReactNode,
  ReactText,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Dropdown, MenuProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import moment from 'moment';
import type { TooltipContentProps } from 'recharts';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportReportToExcel, exportReportToPdf } from '@core/utils/methods';
import {
  OptionProps,
  ProjectDurationBoxPlotPoint,
  ProjectDurationDistributionBucket,
  ProjectDurationReportResponse,
  ProjectDurationRow,
  ProjectDurationTrendPoint,
  ReportsFilters,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type {
  ProjectDurationAverageByGroupChartItem,
  ReportsSummaryMetric,
} from '../../models';
import {
  formatDurationDays,
  FULL_FILTER_CAPTION,
  getProjectDurationAverageByGroupChartData,
  getProjectDurationAverageByGroupTitle,
  getProjectDurationColumns,
  getProjectDurationReportRowClassName,
  getProjectDurationReportRowKey,
  getProjectDurationRowRedirectUrl,
  hasProjectDurationAverageByGroupData,
  hasProjectDurationBoxPlotData,
  hasProjectDurationDistributionData,
  hasProjectDurationTrendData,
  isInteractiveRowClick,
  openReportRowInNewTab,
  PROJECT_DURATION_CHART_COLORS,
} from '../../models';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';

interface ProjectDurationReportProps {
  data?: ProjectDurationReportResponse;
  filters: ReportsFilters;
  groupByOptions: OptionProps[];
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

const CHART_GRID_STROKE = 'rgba(255,255,255,0.12)';
const CHART_AXIS_STROKE = 'rgba(255,255,255,0.65)';

const getTooltipPayload = <T,>({
  active,
  payload,
}: TooltipContentProps): T | undefined =>
  active && payload?.length ? (payload[0]?.payload as T) : undefined;

const renderDistributionTooltip = (props: TooltipContentProps): ReactNode => {
  const bucket = getTooltipPayload<ProjectDurationDistributionBucket>(props);

  if (!bucket) {
    return null;
  }

  return (
    <div className="prov-project-duration-report__tooltip">
      <strong>{bucket.label}</strong>
      <span>{`Min Days: ${formatDurationDays(bucket.minDays)}`}</span>
      <span>{`Max Days: ${formatDurationDays(bucket.maxDays)}`}</span>
      <span>{`Projects: ${bucket.projectCount}`}</span>
    </div>
  );
};

const renderTrendTooltip = (props: TooltipContentProps): ReactNode => {
  const point = getTooltipPayload<ProjectDurationTrendPoint>(props);

  if (!point) {
    return null;
  }

  return (
    <div className="prov-project-duration-report__tooltip">
      <strong>{point.period}</strong>
      <span>
        {`Average Duration Days: ${formatDurationDays(
          point.averageDurationDays,
        )}`}
      </span>
      <span>{`Projects: ${point.projectCount}`}</span>
    </div>
  );
};

const renderAverageByGroupTooltip = (props: TooltipContentProps): ReactNode => {
  const point =
    getTooltipPayload<ProjectDurationAverageByGroupChartItem>(props);

  if (!point) {
    return null;
  }

  return (
    <div className="prov-project-duration-report__tooltip">
      <strong>{point.groupLabel}</strong>
      <span>
        {`Average Duration Days: ${formatDurationDays(
          point.averageDurationDays,
        )}`}
      </span>
      <span>{`Projects: ${point.projectCount}`}</span>
    </div>
  );
};

export const ProjectDurationReport: FC<ProjectDurationReportProps> = ({
  data,
  filters,
  groupByOptions,
  onTableChange,
  sortByOptions,
}: ProjectDurationReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const rows = data?.table?.items ?? [];
  const total = data?.table?.total ?? 0;
  const distribution = data?.charts?.distribution ?? [];
  const trend = data?.charts?.trend ?? [];
  const averageByGroup = data?.charts?.averageByGroup ?? [];
  const boxPlotByProjectType = data?.charts?.boxPlotByProjectType ?? [];
  const averageByGroupChartData = useMemo(
    () => getProjectDurationAverageByGroupChartData(averageByGroup),
    [averageByGroup],
  );
  const averageByGroupTitle = getProjectDurationAverageByGroupTitle(
    filters.groupBy,
    groupByOptions,
    averageByGroup[0]?.groupBy,
  );
  const columns = useMemo(
    () => getProjectDurationColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total Projects',
      value: data?.summary?.totalProjects ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Average Duration Days',
      value: formatDurationDays(data?.summary?.averageDurationDays),
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Median Duration Days',
      value: formatDurationDays(data?.summary?.medianDurationDays),
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Min Duration Days',
      value: formatDurationDays(data?.summary?.minDurationDays),
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Max Duration Days',
      value: formatDurationDays(data?.summary?.maxDurationDays),
    },
  ];

  const onRowClick = useCallback(
    (
      record: ProjectDurationRow,
      event: { target?: EventTarget | null },
    ): void => {
      const redirectUrl = getProjectDurationRowRedirectUrl(record);

      if (!redirectUrl || isInteractiveRowClick(event)) {
        return;
      }

      openReportRowInNewTab(redirectUrl);
    },
    [],
  );

  const onExportExcel = useCallback((): void => {
    exportReportToExcel({
      columns,
      fileName: `Project-Duration-${moment().format('YYYY-MM-DD-HHmm')}.xlsx`,
      metrics,
      reportTitle: 'Project Duration',
      rows,
    });
  }, [columns, metrics, rows]);

  const onExportPdf = useCallback((): void => {
    exportReportToPdf({
      chartsElement: chartsRef.current,
      columns,
      fileName: `Project-Duration-${moment().format('YYYY-MM-DD-HHmm')}.pdf`,
      metrics,
      reportTitle: 'Project Duration',
      rows,
    });
  }, [columns, metrics, rows]);

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-project-duration-report">
      <div className="prov-project-duration-report__toolbar">
        <p className="prov-project-duration-report__description">
          Shows how long each project took from assignment to completion: start
          = earliest category assignment date, end = actual completion (OnAir)
          date. The date filter narrows by completion date only.
        </p>
        <Dropdown disabled={!rows.length} menu={exportMenu}>
          <PrimaryButton title="Export ▾" />
        </Dropdown>
      </div>

      <ReportsSummary metrics={metrics} />

      <div className="prov-project-duration-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={hasProjectDurationDistributionData(distribution)}
          title="Duration Distribution"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={distribution}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="label" stroke={CHART_AXIS_STROKE} />
              <YAxis allowDecimals={false} stroke={CHART_AXIS_STROKE} />
              <Tooltip content={renderDistributionTooltip} />
              <Bar
                dataKey="projectCount"
                fill={PROJECT_DURATION_CHART_COLORS.distribution}
                name="Projects"
              />
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasProjectDurationTrendData(trend)}
          title="Monthly Average Duration"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={trend}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" stroke={CHART_AXIS_STROKE} />
              <YAxis stroke={CHART_AXIS_STROKE} />
              <Tooltip content={renderTrendTooltip} />
              <Line
                dataKey="averageDurationDays"
                name="Average Duration Days"
                stroke={PROJECT_DURATION_CHART_COLORS.trend}
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasProjectDurationAverageByGroupData(averageByGroup)}
          title={averageByGroupTitle}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={averageByGroupChartData}
              layout="vertical"
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis stroke={CHART_AXIS_STROKE} type="number" />
              <YAxis
                dataKey="groupLabel"
                stroke={CHART_AXIS_STROKE}
                type="category"
                width={132}
              />
              <Tooltip content={renderAverageByGroupTooltip} />
              <Bar
                dataKey="averageDurationDays"
                fill={PROJECT_DURATION_CHART_COLORS.averageByGroup}
                name="Average Duration Days"
              />
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasProjectDurationBoxPlotData(boxPlotByProjectType)}
          title="Project Type Duration Quartiles"
        >
          <div className="prov-project-duration-report__box-plot-table-wrap">
            <table className="prov-project-duration-report__box-plot-table">
              <thead>
                <tr>
                  <th>Project Type</th>
                  <th>Min</th>
                  <th>Q1</th>
                  <th>Median</th>
                  <th>Q3</th>
                  <th>Max</th>
                  <th>Projects</th>
                </tr>
              </thead>
              <tbody>
                {boxPlotByProjectType.map(
                  (point: ProjectDurationBoxPlotPoint, index: number) => (
                    <tr
                      key={
                        point.projectTypeId || point.projectType || `${index}`
                      }
                    >
                      <th scope="row">{point.projectType || 'Unassigned'}</th>
                      <td>{formatDurationDays(point.minDurationDays)}</td>
                      <td>{formatDurationDays(point.q1DurationDays)}</td>
                      <td>{formatDurationDays(point.medianDurationDays)}</td>
                      <td>{formatDurationDays(point.q3DurationDays)}</td>
                      <td>{formatDurationDays(point.maxDurationDays)}</td>
                      <td>{point.projectCount}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </ReportChartCard>
      </div>

      <CustomTable
        className="prov-project-duration-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        onRowClick={onRowClick}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowClassName={getProjectDurationReportRowClassName}
        rowKey={getProjectDurationReportRowKey}
        scroll={{ x: 3000, y: 'auto' }}
      />
    </div>
  );
};
